import React, { useState, useMemo } from "react";
import { useParams, useLocation, useHistory } from "react-router-dom";
import { Col, Row, Spin } from "antd";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import styled from "styled-components";
import TabsList from "../../molecules/TabsList";
import ClientInformation from "./ClientInformation";
import GeneralInformation from "./GeneralInformation";
import EMSRecord from "./EMSRecord";
import COICheckProcess from "./COICheckProcess";
import ClientOnboarding from "./ClientOnboarding";
import ClientDocuments from "./ClientDocuments";
import { useScrollFetch } from "hooks/useScrollFetch";
import queryKeys from "common/queryKey";
import { getIsInsytsCrmManagingOffice, getClientid } from "redux/selectors";
import { useSelector } from "react-redux";
import { MAIN_CLIENT_ID } from "common/Constants";

const StyledPage = styled.div`
  padding: 20px;
  background: #fff;
  border-radius: 12px;

  .breadcrumb-link {
    font-size: 16px;
    cursor: pointer;
    color: #444;
    display: inline-flex;
    align-items: center;
  }

  .client-title {
    font-size: 22px;
    font-weight: 700;
    margin: 8px 0 16px 0;
  }

  .client-info-block p {
    margin: 0;
    font-size: 14px;
  }

  .label {
    color: #666;
  }

  .value {
    font-weight: 600;
  }

  .vertical-divider {
    width: 1px;
    background: #ddd;
    height: 40px;
  }
`;

// Route/list segment names that must never be treated as a client id
const LIST_SEGMENTS = ["ownclient", "client", "clients", "client-details"];

// Fields whose boolean value must be shown as Yes / No.
// Add more patterns here (e.g. /strategic/i) if other flags need the same.
const YES_NO_FIELD_PATTERNS = [/influential/i, /star.?customer/i];

const toYesNo = (value) => {
  if (value === true || String(value).toLowerCase() === "true") return "Yes";
  if (value === false || String(value).toLowerCase() === "false") return "No";
  return value;
};

// Returns a copy of the client object with matching boolean fields as Yes/No.
const normalizeYesNoFields = (client) => {
  if (!client || typeof client !== "object") return client;

  return Object.keys(client).reduce((acc, key) => {
    acc[key] = YES_NO_FIELD_PATTERNS.some((p) => p.test(key))
      ? toYesNo(client[key])
      : client[key];
    return acc;
  }, {});
};

const safeDecode = (value) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const isValidId = (value) =>
  typeof value === "string" &&
  value.trim() !== "" &&
  !LIST_SEGMENTS.includes(value.trim().toLowerCase());

// Finds the real client id from route params, query string, navigation state,
// or the URL path (ignoring list segments such as "ownclient").
const resolveClientId = (params, location) => {
  const query = new URLSearchParams(location.search);

  const candidates = [
    params?.clientId,
    params?.clientid,
    params?.id,
    params?.bdo_id,
    ...Object.values(params || {}),
    query.get("clientid"),
    query.get("clientId"),
    query.get("id"),
    location.state?.clientId,
    location.state?.bdo_id,
    location.state?.id,
    ...location.pathname.split("/").filter(Boolean).reverse(),
  ];

  const found = candidates.find(
    (c) => typeof c === "string" && isValidId(safeDecode(c))
  );

  return found ? safeDecode(found).trim() : "";
};

// API shape: { clientName: [ {...client} ], count, LastEvaluatedKey }
const extractClient = (page) =>
  page?.clientName?.[0] ??
  page?.data?.clientName?.[0] ??
  page?.response?.clientName?.[0] ??
  page?.response?.client ??
  page?.client ??
  null;

const ClientDetails = () => {
  const location = useLocation();
  const history = useHistory();
  const params = useParams();

  const isInsytsCrm = useSelector(getIsInsytsCrmManagingOffice);
  const userClientId = useSelector(getClientid);
  // Go back to the list we came from (/ownclient or /client); fall back to the
  // office type when the page is opened directly or refreshed.
  const clientListPath =
    location.state?.from ?? (isInsytsCrm ? "/ownclient" : "/client");

  const clientId = resolveClientId(params, location);
  const { getownClient } = queryKeys;

  // Only Insyts CRM offices hit the CRM client-details endpoint.
  const shouldFetchCrmDetails = Boolean(isInsytsCrm) && !!clientId;

  const { data: clientDetailsPages, isLoading } = useScrollFetch(
    shouldFetchCrmDetails ? getownClient(clientId).key : null,
    shouldFetchCrmDetails
      ? {
        endPoint: `/insyts-crm-clients/client-details`,
        extra: { clientid: clientId, LastEvaluatedKey: 0 },
      }
      : null,
    {
      getNextPageParam: () => null,
      enabled: shouldFetchCrmDetails,
    }
  );

  // Non-CRM fallback: list data passed via navigation state.
  // Supports both history.push(path, { extraData }) and the older
  // history.push(path, { state: { extraData } }) shape.
  const extraData =
    location.state?.extraData ?? location.state?.state?.extraData;
  const nonCrmClientData = Array.isArray(extraData)
    ? extraData.find((item) => item?.bdo_id === clientId) ?? null
    : null;

  const firstPage = clientDetailsPages?.pages?.[0];
  const apiClient = extractClient(firstPage);

  // Influential / Star Customer: true -> "Yes", false -> "No"
  const clientData = useMemo(
    () => normalizeYesNoFields(apiClient ?? nonCrmClientData),
    [apiClient, nonCrmClientData]
  );

  const [tabId, setTabId] = useState("Client Information");

  const clientTabs = useMemo(
    () => [
      {
        id: "Client Information",
        label: "Client Information",
        // key re-mounts the form when data arrives so initial values apply
        component: (
          <ClientInformation
            key={clientData?.bdo_id ?? "empty"}
            filteredData={clientData}
          />
        ),
      },
      {
        id: "General Details",
        label: "General Details",
        component: (
          <GeneralInformation
            key={clientData?.bdo_id ?? "empty"}
            filteredData={clientData}
          />
        ),
      },
      {
        id: "Client OnBoarding",
        label: "Client OnBoarding",
        component: <ClientOnboarding data={clientData} />,
      },
      {
        id: "COI Check Process",
        label: "COI Check Process",
        component: <COICheckProcess data={clientData} />,
      },
      {
        id: "EMS Records",
        label: "EMS Records",
        component: <EMSRecord filteredData={clientData} />,
      },
      ...(userClientId !== MAIN_CLIENT_ID
        ? [
          {
            id: "Document",
            label: "Document",
            component: <ClientDocuments filteredData={clientData} />,
          },
        ]
        : []),
    ],
    [clientData, userClientId]
  );

  if (shouldFetchCrmDetails && isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", marginTop: "100px" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <StyledPage>
      <div className="breadcrumb-link" onClick={() => history.push(clientListPath)}>
        Client <ArrowForwardIosIcon fontSize="small" />
      </div>

      <div className="client-title">{clientData?.name ?? "-"}</div>

      <Row gutter={24} align="middle" className="client-info-block" style={{ marginBottom: 20 }}>
        <Col>
          <p className="value">{clientData?.bdo_id ?? "-"}</p>
          <p className="label">Client ID</p>
        </Col>

        <Col>
          <div className="vertical-divider" />
        </Col>

        <Col>
          <p className="value">
            {clientData?.["statecode@OData.Community.Display.V1.FormattedValue"] ?? "-"}
          </p>
          <p className="label">Status Reason</p>
        </Col>

        <Col>
          <div className="vertical-divider" />
        </Col>

        <Col>
          <p className="value">
            {clientData?.["bdo_status@OData.Community.Display.V1.FormattedValue"] ?? "-"}
          </p>
          <p className="label">COB FORM Status</p>
        </Col>

        <Col>
          <div className="vertical-divider" />
        </Col>

        <Col>
          <p className="value" style={{ color: "#1677ff" }}>
            {clientData?.["_ownerid_value@OData.Community.Display.V1.FormattedValue"] ??
              clientData?.createdByName ??
              "-"}
          </p>
          <p className="label">Owner</p>
        </Col>
      </Row>

      <TabsList items={clientTabs} handleTabSelect={setTabId} tabId={tabId} />
    </StyledPage>
  );
};

export default ClientDetails;